import {useState} from 'react';

import {CardWidgetBanner} from 'shells/card/card-widget-banner/card-widget-banner';
import {PersonSearchRegularAiIcon} from 'shells/icon';
import {Body} from 'shells/typography/body';

import type {GetContactEnrichment_contactEnrichment as ContactEnrichment} from 'nutshell-graphql-types';

import {useEnrichContact} from '../../../../nutshell-intelligence-enrichment/contact-enrichment/graphql/hooks';
import {formatData} from '../../../../nutshell-intelligence-enrichment/contact-enrichment/helpers';
import {
    ReviewEnrichmentDataModal,
    type EnrichmentResponseStatus,
} from '../../../../nutshell-intelligence-enrichment/review-enrichment-data-modal/review-enrichment-data-modal';

import {contactRefetchForEvent} from '../../hooks/use-contact-update-refetch';

interface Props {
    contactId: string;
    contactName: string | null | undefined;
    contactEnrichment: ContactEnrichment;
    onDismiss: () => void;
}

export function ContactPeopleIqBanner(props: Props) {
    const [isShowingModal, setIsShowingModal] = useState<boolean>(false);
    const [actionStatus, setActionStatus] = useState<EnrichmentResponseStatus | null | undefined>(
        undefined
    );

    const {enrichContact, isLoading, error} = useEnrichContact();

    const {mainFields, additionalFields} = formatData(props.contactEnrichment);
    const entityName = props.contactName ?? 'this contact';

    const onContactEnrichmentRespond = (
        isBaseFieldsAccepted: boolean,
        isSocialProfilesChecked: boolean,
        isRelatedCompanyChecked: boolean
    ) => {
        const anyFieldsAccepted =
            isBaseFieldsAccepted || isRelatedCompanyChecked || isSocialProfilesChecked;
        setActionStatus(anyFieldsAccepted ? 'ACCEPTED' : 'DECLINED');
        enrichContact({
            contactId: props.contactId,
            baseFieldsResponse: isBaseFieldsAccepted ? 'ACCEPTED' : 'DECLINED',
            companyFieldsResponse: isRelatedCompanyChecked ? 'ACCEPTED' : 'DECLINED',
            socialFieldsResponse: isSocialProfilesChecked ? 'ACCEPTED' : 'DECLINED',
        })
            .then(() => {
                props.onDismiss();
                setIsShowingModal(false);
                if (anyFieldsAccepted) {
                    contactRefetchForEvent('contactEnrichmentUpdated');
                }
            })
            .catch(() => {
                setActionStatus('ERROR');
            });
    };

    return (
        <>
            <CardWidgetBanner
                key='peep-contact-enrichment'
                title='People IQ'
                secondaryTitle={
                    <>
                        Additional information found about{' '}
                        <Body isBold={true} size='small'>
                            {entityName || 'this contact'}
                        </Body>
                        .
                    </>
                }
                titleIcon={PersonSearchRegularAiIcon}
                actionLabel='Review'
                onClick={() => setIsShowingModal(true)}
                onDismiss={props.onDismiss}
                widgetType='NUTSHELL_IQ'
            />
            {isShowingModal && (
                <ReviewEnrichmentDataModal
                    entityName={props.contactName}
                    onClose={() => setIsShowingModal(false)}
                    data={mainFields}
                    additionalFields={{...additionalFields}}
                    onSave={(isSocialProfilesChecked, isRelatedCompanyChecked) => {
                        if (isSocialProfilesChecked != null && isRelatedCompanyChecked != null) {
                            onContactEnrichmentRespond(
                                true,
                                isSocialProfilesChecked,
                                isRelatedCompanyChecked
                            );
                        }
                    }}
                    onDecline={() => {
                        onContactEnrichmentRespond(false, false, false);
                    }}
                    error={error}
                    loading={isLoading}
                    actionStatus={actionStatus}
                    setActionStatus={setActionStatus}
                />
            )}
        </>
    );
}
